import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import NeedAttentionAddFamiliesModal from "./NeedAttentionAddFamiliesModal";
import DeleteFamilyModal from "./DeleteFamilyModal";
import { selectUserToken, selectUserData } from "../../../../../features/userSlice";
import { familiesEndPointLogic } from "../../../../../logic/DataCollection/Families/FamiliesEndPointLogic";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useWebsiteSharedUid } from "../../../../../customHooks/useWebsiteSharedUid";

// ---- Types ----
export interface FamilyItem {
  id: string | number;
  name: string;
  count: number;
  provider: string;
  configs: number;
  brands: number;
  backendInterfaceId: string | number;
  step: "fetcher" | "unifier" | "advisor";
}

interface FamilyListCardProps {
  title: string;
  totalCount: number;
  items: FamilyItem[];
  actionType: "edit" | "add"; // left icon: real link (opens in new tab) vs modal
  onAction?: (item: FamilyItem) => void; // actionType === "add"
  getActionHref?: (item: FamilyItem) => string; // actionType === "edit"
  onDelete: (item: FamilyItem) => void;
  actionTrackerId: string;
  deleteTrackerId: string;
  hasPermission: boolean;
}

// Action-tracking ids, keyed by step (lowercased speciesTitle).
const BRAND_CONFIG_LINK_TRACKER: Record<string, string> = {
  fetcher: "1018",
  unifier: "1019",
  advisor: "1020",
};
const DELETE_TRACKER: Record<string, string> = {
  fetcher: "1021",
  unifier: "1022",
  advisor: "1023",
};
const ADD_CONFIG_TRACKER: Record<string, string> = {
  fetcher: "1024",
  unifier: "1025",
  advisor: "1026",
};

interface NeedAttentionActionProps {
  speciesTitle: string;
  missingItem: string;
  emptyFamilies: FamilyItem[];
  missingFamilies: FamilyItem[];
  emptyMessage: string;
  missingMessage: string;
}

// ---- Success state (shown instead of the list once it's empty) ----
function SuccessCard({ message }: { message: string }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #0000001F",
        borderRadius: "4px",
        minHeight: 66,
        p: 2,
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#2E7D32", textAlign: "center" }}>
        {message}
      </Typography>
    </Box>
  );
}

// ---- Reusable card ----
function FamilyListCard({
  title,
  totalCount,
  items,
  actionType,
  onAction,
  getActionHref,
  onDelete,
  actionTrackerId,
  deleteTrackerId,
  hasPermission,
}: FamilyListCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: "4px",
        borderColor: "#0000001F",
        overflow: "hidden",
        flex: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: "1rem" }}>
          {title}
        </Typography>
        <Typography sx={{ fontWeight: 500, fontSize: "1rem" }}>
          {totalCount}
        </Typography>
      </Box>
      <Divider />

      {/* Rows */}
      {items.map((item, index) => (
        <Box key={item.id}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
            }}
          >
            <Stack direction="column">
              <Typography component="span" sx={{ fontSize: "14px" }}>
                {item.name}
                <span
                  style={{
                    color: "#00000061",
                    marginLeft: '5px',
                    fontSize: "14px",
                  }}
                >
                  ({item.count})
                </span>
              </Typography>

              <Typography sx={{ color: "text.secondary", fontSize: "12px" }}>
                {item.provider} - {item.configs} config
                {item.configs === 1 ? "" : "s"} - {item.brands} brand
                {item.brands === 1 ? "" : "s"}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5}>
              {actionType === "edit" ? (
                <IconButton
                  size="small"
                  component="a"
                  href={getActionHref?.(item)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tracker={actionTrackerId}
                  sx={{ color: "#9C27B0" }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => onAction?.(item)}
                  data-tracker={actionTrackerId}
                  disabled={!hasPermission}
                  title={hasPermission ? undefined : "You don't have permission to edit families."}
                  sx={{ color: "#9C27B0" }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => onDelete(item)}
                data-tracker={deleteTrackerId}
                disabled={!hasPermission}
                title={hasPermission ? undefined : "You don't have permission to delete families."}
                sx={{ color: "#D32F2F" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
          {index < items.length - 1 && <Divider />}
        </Box>
      ))}
    </Paper>
  );
}

// ---- Simple standalone component ----
export default function NeedAttentionFamiliesAction({
  speciesTitle,
  missingItem,
  emptyFamilies,
  missingFamilies,
  emptyMessage,
  missingMessage,
}: NeedAttentionActionProps) {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  // The EP treats website_shared_uid as a required dummy input — pass the
  // first website_shared_uid the user can access from /me, same as the
  // initial Q4 fetch in NeedAttention.tsx.
  const website_shared_uid = useWebsiteSharedUid();
  const userData = useSelector(selectUserData);
  const hasCreateEditDeleteFamiliesPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "businesse_advisor",
    "create_edit_delete_families",
  );
  const [addFamilyItem, setAddFamilyItem] = React.useState<FamilyItem | null>(null);

  const refetch = () => {
    familiesEndPointLogic.getNeedsAttentionSummary(
      oauthTokenHeader,
      website_shared_uid,
      dispatch,
    );
  };

  const handleOpenModal = (item: FamilyItem) => {
    setAddFamilyItem(item);
  };

  const handleCloseModal = () => {
    setAddFamilyItem(null);
  };

  // Doc: "Clicking the purple icon directs the user to the Brands screen" —
  // this is the Empty Families list's "Add brands" action; brand-family
  // membership is automatched, so there's no in-app add-brands flow here,
  // just this redirect. An empty family has no brands to target directly, so
  // scope the screen to its interface instead (its "Affiliate System" filter
  // is seeded via `?affiliateSystem=<name>`, matched against affiliate_system).
  // A real href (not a navigate() call) so the browser's own "open in new
  // tab"/"copy link" context menu works on right-click too.
  const getBrandsScreenHref = (item: FamilyItem) =>
    `/operations/brands?tab=all_brands&affiliateSystem=${encodeURIComponent(item.provider)}`;

  const stepKey = speciesTitle.toLowerCase();

  const [deleteFamilyItem, setDeleteFamilyItem] = React.useState<FamilyItem | null>(
    null,
  );
  const handleOpenDeleteFamilyModal = (item: FamilyItem) => {
    setDeleteFamilyItem(item);
  };
  const handleCloseDeleteFamilyModal = () => {
    setDeleteFamilyItem(null);
  };

  return (
    <Stack direction="row" spacing={2} pb={2} px={2}>
      {emptyFamilies.length === 0 ? (
        <SuccessCard message={emptyMessage} />
      ) : (
        <FamilyListCard
          title={`Empty ${speciesTitle} Families`}
          totalCount={emptyFamilies.length}
          items={emptyFamilies}
          actionType="edit"
          getActionHref={getBrandsScreenHref}
          onDelete={handleOpenDeleteFamilyModal}
          actionTrackerId={BRAND_CONFIG_LINK_TRACKER[stepKey]}
          deleteTrackerId={DELETE_TRACKER[stepKey]}
          hasPermission={hasCreateEditDeleteFamiliesPermission}
        />
      )}
      <DeleteFamilyModal
        open={!!deleteFamilyItem}
        handleClose={handleCloseDeleteFamilyModal}
        item={deleteFamilyItem}
        onDeleted={refetch}
      />
      <Box sx={{ flex: 1 }}>
        {missingFamilies.length === 0 ? (
          <SuccessCard message={missingMessage} />
        ) : (
          <FamilyListCard
            title={`${speciesTitle} Families Missing ${missingItem}`}
            totalCount={missingFamilies.length}
            items={missingFamilies}
            actionType="add"
            onAction={handleOpenModal}
            onDelete={handleOpenDeleteFamilyModal}
            actionTrackerId={ADD_CONFIG_TRACKER[stepKey]}
            deleteTrackerId={DELETE_TRACKER[stepKey]}
            hasPermission={hasCreateEditDeleteFamiliesPermission}
          />
        )}
        <NeedAttentionAddFamiliesModal
          missingItem={missingItem}
          stepTitle={speciesTitle}
          open={!!addFamilyItem}
          familyId={addFamilyItem?.id}
          familyName={addFamilyItem?.name}
          stepKey={addFamilyItem?.step}
          backendInterfaceId={addFamilyItem?.backendInterfaceId}
          brandsCount={addFamilyItem?.brands ?? 0}
          existingMapIds={[]}
          onSubmitted={refetch}
          handleClose={handleCloseModal}
        />
      </Box>
    </Stack>
  );
}
